"use client";

import { useState } from 'react';
import { HOURS_PER_MONTH, PANEL_URL } from '@/lib/panelPrices';
import type { PageTexts } from '@/lib/siteTexts';

interface ResourceType {
  id: string;
  name: string;
  unit: string;
  icon: string;
  cloudstack_name: string;
  hourly_price?: number | null;
}

interface ConfigurationResource {
  id: string;
  resource_type_id: string;
  quantity: number;
  resource_types: ResourceType | null;
}

interface Configuration {
  id: string;
  name: string;
  group_id: string | null;
  description: string | null;
  price: number | null;
  display_order: number;
  configuration_resources: ConfigurationResource[];
}

function computeConfigPrice(config: Configuration): number | null {
  const resources = config.configuration_resources;
  if (!resources || resources.length === 0) return config.price;
  const hasHourlyPrice = resources.some(r => r.resource_types?.hourly_price != null);
  if (!hasHourlyPrice) return config.price;
  const computed = resources.reduce((total, res) => {
    const hp = res.resource_types?.hourly_price ?? 0;
    return total + hp * res.quantity * HOURS_PER_MONTH;
  }, 0);
  return computed > 0 ? computed : config.price;
}

export default function ConfigCard({ config, texts: t }: { config: Configuration; texts: PageTexts<'configurations'> }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const displayPrice = computeConfigPrice(config);

  const handleStart = async () => {
    try {
      setSubmitting(true);
      setSubmitError(null);

      const configurationData = {
        name: config.name,
        description: config.description,
        resources: config.configuration_resources.map(res => ({
          id: res.id,
          resource_type_id: res.resource_type_id,
          label: res.resource_types?.name || '',
          unit: res.resource_types?.unit || '',
          cloudstack_name: res.resource_types?.cloudstack_name || '',
          quantity: res.quantity,
          value: res.quantity,
        })),
        price: displayPrice,
      };

      const payload = {
        mode: 'template',
        config_json: configurationData,
      };

      const response = await fetch(`${PANEL_URL}/api/signup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
        redirect: 'manual',
      });

      console.log('status', response.status, 'redirected', response.redirected, 'url', response.url);

      if (response.ok) {
        const data = await response.json();
        const token = data.token;
        window.location.assign(
          `${PANEL_URL}/auth/redirect/saml2?intent=${encodeURIComponent(token)}`
        );
      } else if (response.type === 'opaqueredirect' || response.status === 0) {
        window.location.assign(`${PANEL_URL}/api/signup-intent`);
      } else {
        const errorText = await response.text();
        setSubmitError(`${t['card.error']} ${response.status}. ${errorText || t['card.errorRetry']}`);
      }
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitError(t['card.offline']);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[rgba(255,255,255,0.03)] border border-[rgba(79,124,255,0.15)] rounded-2xl p-6 hover:border-[rgba(0,212,255,0.3)] hover:bg-[rgba(0,212,255,0.03)] transition-all duration-300 flex flex-col">
      <div className="mb-4">
        <h3 className="text-xl font-bold text-white">{config.name}</h3>
        {config.description && (
          <p className="text-[#8BA3CC] text-sm mt-1">{config.description}</p>
        )}
      </div>

      {/* Resources */}
      {config.configuration_resources && config.configuration_resources.length > 0 && (
        <div className="space-y-2.5 flex-1">
          {config.configuration_resources.map(res => (
            <div key={res.id} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-base">{res.resource_types?.icon || '📦'}</span>
                <span className="text-[#8BA3CC] text-sm">{res.resource_types?.name || '—'}</span>
              </div>
              <span className="text-white font-semibold text-sm">
                {res.quantity} {res.resource_types?.unit || ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Price */}
      {displayPrice != null && (
        <div className="mt-5 pt-4 border-t border-[rgba(79,124,255,0.15)]">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-[#00D4FF]">
              {Number(displayPrice).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
            </span>
            <span className="text-[#8BA3CC] text-sm">сом/мес</span>
          </div>
        </div>
      )}

      {/* Button */}
      <div className="mt-4">
        <button
          onClick={handleStart}
          disabled={submitting}
          className="w-full btn-primary py-3 rounded-xl font-bold text-sm transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
              <span>{t['card.sending']}</span>
            </>
          ) : (
            t['card.start']
          )}
        </button>

        {submitError && (
          <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-xs text-center">{submitError}</p>
          </div>
        )}
      </div>
    </div>
  );
}
